import { useCallback, useEffect, useRef } from "react";
import type { ClientAgentMessage } from "@/types/agent";
import { MessageBubble } from "./message-bubble";
import { WelcomeState } from "./welcome-state";

interface MessageListProps {
  messages: ClientAgentMessage[];
  isStreaming: boolean;
  onApproveProposal: (proposalId: string) => void;
  onRejectProposal: (proposalId: string) => void;
  onSuggestionClick: (text: string) => void;
}

export function MessageList({
  messages,
  isStreaming,
  onApproveProposal,
  onRejectProposal,
  onSuggestionClick,
}: MessageListProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);

  const isNearBottom = useCallback((): boolean => {
    const el = containerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (!userScrolledUp.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleScroll = useCallback(() => {
    userScrolledUp.current = !isNearBottom();
  }, [isNearBottom]);

  // Scroll when new messages are added
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on new messages only
  useEffect(() => {
    userScrolledUp.current = false;
    scrollToBottom();
  }, [messages.length]);

  // Scroll during streaming only if user hasn't scrolled up
  useEffect(() => {
    if (!isStreaming) return;
    const timer = setInterval(() => {
      if (!userScrolledUp.current) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }, 300);
    return () => clearInterval(timer);
  }, [isStreaming]);

  if (messages.length === 0) {
    return <WelcomeState onAction={onSuggestionClick} />;
  }

  let lastAssistantIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === "assistant") {
      lastAssistantIdx = i;
      break;
    }
  }

  return (
    <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-3 space-y-4">
      {messages.map((msg, i) => {
        const isLast = i === messages.length - 1;
        return (
          <MessageBubble
            key={msg.id}
            message={msg}
            isStreaming={isStreaming && isLast && msg.role === "assistant"}
            isLastAssistant={i === lastAssistantIdx}
            onApproveProposal={onApproveProposal}
            onRejectProposal={onRejectProposal}
            onSuggestionClick={onSuggestionClick}
          />
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
